/**
 * 저장된 카드 관련 번역
 * @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md
 */
import type { Language, Locale } from '@/config/locales';

export interface SavedCardsTranslation {
  // PaymentMethodsSection
  paymentMethod: string;
  selectCard: string;
  registerNewCard: string;
  defaultCard: string;
  expired: string;
  loading: string;
  noCards: string;

  // CardNicknameModal
  editNickname: string;
  editNicknameSubtitle: string;
  nicknamePlaceholder: string;
  clearNickname: string;
  save: string;
  saving: string;
  saveError: string;

  // DeleteCardConfirmModal
  deleteCardTitle: string;
  deleteCardMessage: string;
  deleteCardDefaultWarning: string;
  delete: string;
  deleting: string;

  // AddCardModal
  addCardTitle: string;
  addCardSubtitle: string;
  creatingCheckout: string;
  confirmingRegistration: string;
  successTitle: string;
  successMessage: string;
  errorTitle: string;
  errorMessage: string;
  retry: string;
  done: string;
  cancel: string;
  nicknameLabel: string;
  skipNickname: string;
  saveWithNickname: string;
  cardNumberLabel: string;
  expiryMonthLabel: string;
  expiryYearLabel: string;
  cvvLabel: string;
  holderLabel: string;
  invalidCard: string;
  invalidExpiry: string;
  invalidCvv: string;
  invalidHolder: string;

  // 에러 메시지
  errorCardLimitExceeded: string;
  errorDuplicateCard: string;
  errorCardInactive: string;
  errorCardExpired: string;
  errorCardNotFound: string;
  errorRegistrationFailed: string;
}

// #512 — Locale 단일 출처 정합.
export type SavedCardsTranslations =
  & Record<Language, SavedCardsTranslation>
  & Record<Exclude<Locale, Language>, SavedCardsTranslation>;

export const savedCardsTranslations: SavedCardsTranslations = {
  ko: {
    // PaymentMethodsSection
    paymentMethod: '결제 수단',
    selectCard: '카드를 선택하세요',
    registerNewCard: '새 카드 등록',
    defaultCard: '기본 카드',
    expired: '만료됨',
    loading: '로딩 중...',
    noCards: '등록된 카드가 없습니다',

    // CardNicknameModal
    editNickname: '카드 별명 수정',
    editNicknameSubtitle: '카드를 쉽게 구분할 수 있는 이름을 설정하세요',
    nicknamePlaceholder: '예: 개인 카드, 회사 카드',
    clearNickname: '별명 삭제',
    save: '저장',
    saving: '저장 중...',
    saveError: '별명 저장에 실패했습니다. 다시 시도해 주세요.',

    // DeleteCardConfirmModal
    deleteCardTitle: '카드 삭제',
    deleteCardMessage: '이 카드를 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.',
    deleteCardDefaultWarning: '이 카드는 기본 결제 수단입니다. 삭제 후 다른 카드가 기본으로 설정됩니다.',
    delete: '삭제',
    deleting: '삭제 중...',

    // AddCardModal
    addCardTitle: '카드 등록',
    addCardSubtitle: '결제에 사용할 새 카드를 등록합니다',
    creatingCheckout: '결제 양식을 준비하는 중...',
    confirmingRegistration: '카드를 등록하는 중...',
    successTitle: '카드 등록 완료',
    successMessage: '새 카드가 성공적으로 등록되었습니다.',
    errorTitle: '등록 실패',
    errorMessage: '카드 등록 중 오류가 발생했습니다.',
    retry: '다시 시도',
    done: '완료',
    cancel: '취소',
    nicknameLabel: '카드 별명을 입력하시겠습니까? (선택)',
    skipNickname: '별명 없이 저장',
    saveWithNickname: '별명과 함께 저장',
    cardNumberLabel: '카드 번호',
    expiryMonthLabel: '월',
    expiryYearLabel: '연도',
    cvvLabel: 'CVV',
    holderLabel: '카드 소유자',
    invalidCard: '유효하지 않은 카드 번호입니다.',
    invalidExpiry: '유효하지 않은 만료일입니다.',
    invalidCvv: '유효하지 않은 CVV입니다.',
    invalidHolder: '카드 소유자 이름을 입력해 주세요.',

    // 에러 메시지
    errorCardLimitExceeded: '최대 카드 등록 수를 초과했습니다. 기존 카드를 삭제한 후 다시 시도해 주세요.',
    errorDuplicateCard: '이미 등록된 카드입니다.',
    errorCardInactive: '비활성화된 카드입니다.',
    errorCardExpired: '만료된 카드입니다.',
    errorCardNotFound: '카드를 찾을 수 없습니다.',
    errorRegistrationFailed: '카드 등록에 실패했습니다. 다시 시도해 주세요.',
  },

  es: {
    // PaymentMethodsSection
    paymentMethod: 'Método de Pago',
    selectCard: 'Selecciona una tarjeta',
    registerNewCard: 'Registrar nueva tarjeta',
    defaultCard: 'Tarjeta predeterminada',
    expired: 'Expirada',
    loading: 'Cargando...',
    noCards: 'No hay tarjetas registradas',

    // CardNicknameModal
    editNickname: 'Editar nombre de tarjeta',
    editNicknameSubtitle: 'Establece un nombre para identificar fácilmente esta tarjeta',
    nicknamePlaceholder: 'Ej: Tarjeta personal, Tarjeta de empresa',
    clearNickname: 'Eliminar nombre',
    save: 'Guardar',
    saving: 'Guardando...',
    saveError: 'Error al guardar el nombre. Por favor, inténtalo de nuevo.',

    // DeleteCardConfirmModal
    deleteCardTitle: 'Eliminar tarjeta',
    deleteCardMessage: '¿Estás seguro de eliminar esta tarjeta? Esta acción no se puede deshacer.',
    deleteCardDefaultWarning: 'Esta es tu tarjeta predeterminada. Otra tarjeta será establecida como predeterminada.',
    delete: 'Eliminar',
    deleting: 'Eliminando...',

    // AddCardModal
    addCardTitle: 'Registrar tarjeta',
    addCardSubtitle: 'Registra una nueva tarjeta para pagos',
    creatingCheckout: 'Preparando el formulario de pago...',
    confirmingRegistration: 'Registrando tarjeta...',
    successTitle: 'Tarjeta registrada',
    successMessage: 'Tu nueva tarjeta ha sido registrada exitosamente.',
    errorTitle: 'Error de registro',
    errorMessage: 'Ocurrió un error al registrar la tarjeta.',
    retry: 'Reintentar',
    done: 'Listo',
    cancel: 'Cancelar',
    nicknameLabel: '¿Deseas agregar un nombre a esta tarjeta? (Opcional)',
    skipNickname: 'Guardar sin nombre',
    saveWithNickname: 'Guardar con nombre',
    cardNumberLabel: 'Número de tarjeta',
    expiryMonthLabel: 'Mes',
    expiryYearLabel: 'Año',
    cvvLabel: 'CVV',
    holderLabel: 'Titular',
    invalidCard: 'Número de tarjeta inválido',
    invalidExpiry: 'Fecha de vencimiento inválida',
    invalidCvv: 'CVV inválido',
    invalidHolder: 'Ingresa el titular de la tarjeta',

    // 에러 메시지
    errorCardLimitExceeded: 'Has alcanzado el límite máximo de tarjetas. Elimina una tarjeta existente e inténtalo de nuevo.',
    errorDuplicateCard: 'Esta tarjeta ya está registrada.',
    errorCardInactive: 'Esta tarjeta está desactivada.',
    errorCardExpired: 'Esta tarjeta ha expirado.',
    errorCardNotFound: 'Tarjeta no encontrada.',
    errorRegistrationFailed: 'Error al registrar la tarjeta. Por favor, inténtalo de nuevo.',
  },

  en: {
    // PaymentMethodsSection
    paymentMethod: 'Payment Method',
    selectCard: 'Select a card',
    registerNewCard: 'Register new card',
    defaultCard: 'Default card',
    expired: 'Expired',
    loading: 'Loading...',
    noCards: 'No cards registered',

    // CardNicknameModal
    editNickname: 'Edit card nickname',
    editNicknameSubtitle: 'Set a name to easily identify this card',
    nicknamePlaceholder: 'e.g., Personal card, Work card',
    clearNickname: 'Remove nickname',
    save: 'Save',
    saving: 'Saving...',
    saveError: 'Failed to save nickname. Please try again.',

    // DeleteCardConfirmModal
    deleteCardTitle: 'Delete card',
    deleteCardMessage: 'Are you sure you want to delete this card? This action cannot be undone.',
    deleteCardDefaultWarning: 'This is your default card. Another card will be set as default.',
    delete: 'Delete',
    deleting: 'Deleting...',

    // AddCardModal
    addCardTitle: 'Register card',
    addCardSubtitle: 'Register a new card for payments',
    creatingCheckout: 'Preparing payment form...',
    confirmingRegistration: 'Registering card...',
    successTitle: 'Card registered',
    successMessage: 'Your new card has been registered successfully.',
    errorTitle: 'Registration failed',
    errorMessage: 'An error occurred while registering the card.',
    retry: 'Retry',
    done: 'Done',
    cancel: 'Cancel',
    nicknameLabel: 'Would you like to add a nickname? (Optional)',
    skipNickname: 'Save without nickname',
    saveWithNickname: 'Save with nickname',
    cardNumberLabel: 'Card number',
    expiryMonthLabel: 'Month',
    expiryYearLabel: 'Year',
    cvvLabel: 'CVV',
    holderLabel: 'Cardholder name',
    invalidCard: 'Invalid card number',
    invalidExpiry: 'Invalid expiration date',
    invalidCvv: 'Invalid CVV',
    invalidHolder: 'Enter the cardholder name',

    // 에러 메시지
    errorCardLimitExceeded: 'You have reached the maximum number of cards. Please delete an existing card and try again.',
    errorDuplicateCard: 'This card is already registered.',
    errorCardInactive: 'This card is inactive.',
    errorCardExpired: 'This card has expired.',
    errorCardNotFound: 'Card not found.',
    errorRegistrationFailed: 'Failed to register the card. Please try again.',
  },

  pt: {
    paymentMethod: 'Método de pagamento',
    selectCard: 'Selecione um cartão',
    registerNewCard: 'Registrar novo cartão',
    defaultCard: 'Cartão padrão',
    expired: 'Expirado',
    loading: 'Carregando...',
    noCards: 'Nenhum cartão registrado',
    editNickname: 'Editar apelido do cartão',
    editNicknameSubtitle: 'Defina um nome para identificar facilmente este cartão',
    nicknamePlaceholder: 'Ex: Cartão pessoal, Cartão da empresa',
    clearNickname: 'Remover apelido',
    save: 'Salvar',
    saving: 'Salvando...',
    saveError: 'Falha ao salvar o apelido. Por favor, tente novamente.',
    deleteCardTitle: 'Excluir cartão',
    deleteCardMessage: 'Tem certeza de que deseja excluir este cartão? Esta ação não pode ser desfeita.',
    deleteCardDefaultWarning: 'Este é o seu cartão padrão. Outro cartão será definido como padrão.',
    delete: 'Excluir',
    deleting: 'Excluindo...',
    addCardTitle: 'Registrar cartão',
    addCardSubtitle: 'Registre um novo cartão para pagamentos',
    creatingCheckout: 'Preparando o formulário de pagamento...',
    confirmingRegistration: 'Registrando cartão...',
    successTitle: 'Cartão registrado',
    successMessage: 'Seu novo cartão foi registrado com sucesso.',
    errorTitle: 'Falha no registro',
    errorMessage: 'Ocorreu um erro ao registrar o cartão.',
    retry: 'Tentar novamente',
    done: 'Concluído',
    cancel: 'Cancelar',
    nicknameLabel: 'Deseja adicionar um apelido? (Opcional)',
    skipNickname: 'Salvar sem apelido',
    saveWithNickname: 'Salvar com apelido',
    cardNumberLabel: 'Número do cartão',
    expiryMonthLabel: 'Mês',
    expiryYearLabel: 'Ano',
    cvvLabel: 'CVV',
    holderLabel: 'Titular',
    invalidCard: 'Número do cartão inválido',
    invalidExpiry: 'Data de validade inválida',
    invalidCvv: 'CVV inválido',
    invalidHolder: 'Informe o titular do cartão',
    errorCardLimitExceeded: 'Você atingiu o número máximo de cartões. Exclua um cartão existente e tente novamente.',
    errorDuplicateCard: 'Este cartão já está registrado.',
    errorCardInactive: 'Este cartão está inativo.',
    errorCardExpired: 'Este cartão expirou.',
    errorCardNotFound: 'Cartão não encontrado.',
    errorRegistrationFailed: 'Falha ao registrar o cartão. Por favor, tente novamente.',
  },

  fr: {
    paymentMethod: 'Mode de paiement',
    selectCard: 'Sélectionnez une carte',
    registerNewCard: 'Enregistrer une nouvelle carte',
    defaultCard: 'Carte par défaut',
    expired: 'Expirée',
    loading: 'Chargement...',
    noCards: 'Aucune carte enregistrée',
    editNickname: 'Modifier le surnom de la carte',
    editNicknameSubtitle: 'Définissez un nom pour identifier facilement cette carte',
    nicknamePlaceholder: 'Ex : Carte personnelle, Carte professionnelle',
    clearNickname: 'Supprimer le surnom',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    saveError: 'Échec de l’enregistrement du surnom. Veuillez réessayer.',
    deleteCardTitle: 'Supprimer la carte',
    deleteCardMessage: 'Voulez-vous vraiment supprimer cette carte ? Cette action est irréversible.',
    deleteCardDefaultWarning: 'Il s’agit de votre carte par défaut. Une autre carte sera définie comme carte par défaut.',
    delete: 'Supprimer',
    deleting: 'Suppression...',
    addCardTitle: 'Enregistrer une carte',
    addCardSubtitle: 'Enregistrez une nouvelle carte pour les paiements',
    creatingCheckout: 'Préparation du formulaire de paiement...',
    confirmingRegistration: 'Enregistrement de la carte...',
    successTitle: 'Carte enregistrée',
    successMessage: 'Votre nouvelle carte a été enregistrée avec succès.',
    errorTitle: 'Échec de l’enregistrement',
    errorMessage: 'Une erreur s’est produite lors de l’enregistrement de la carte.',
    retry: 'Réessayer',
    done: 'Terminé',
    cancel: 'Annuler',
    nicknameLabel: 'Souhaitez-vous ajouter un surnom ? (Facultatif)',
    skipNickname: 'Enregistrer sans surnom',
    saveWithNickname: 'Enregistrer avec surnom',
    cardNumberLabel: 'Numéro de carte',
    expiryMonthLabel: 'Mois',
    expiryYearLabel: 'Année',
    cvvLabel: 'CVV',
    holderLabel: 'Titulaire',
    invalidCard: 'Numéro de carte invalide',
    invalidExpiry: 'Date d’expiration invalide',
    invalidCvv: 'CVV invalide',
    invalidHolder: 'Saisissez le titulaire de la carte',
    errorCardLimitExceeded: 'Vous avez atteint le nombre maximum de cartes. Supprimez une carte existante et réessayez.',
    errorDuplicateCard: 'Cette carte est déjà enregistrée.',
    errorCardInactive: 'Cette carte est inactive.',
    errorCardExpired: 'Cette carte a expiré.',
    errorCardNotFound: 'Carte introuvable.',
    errorRegistrationFailed: 'Échec de l’enregistrement de la carte. Veuillez réessayer.',
  },

  zh: {
    paymentMethod: '支付方式',
    selectCard: '选择一张卡',
    registerNewCard: '注册新卡',
    defaultCard: '默认卡',
    expired: '已过期',
    loading: '加载中...',
    noCards: '没有已注册的卡',
    editNickname: '编辑卡昵称',
    editNicknameSubtitle: '设置一个名称以便轻松识别此卡',
    nicknamePlaceholder: '例如：个人卡、公司卡',
    clearNickname: '删除昵称',
    save: '保存',
    saving: '保存中...',
    saveError: '保存昵称失败。请重试。',
    deleteCardTitle: '删除卡',
    deleteCardMessage: '您确定要删除此卡吗?此操作无法撤销。',
    deleteCardDefaultWarning: '这是您的默认卡。另一张卡将被设置为默认。',
    delete: '删除',
    deleting: '删除中...',
    addCardTitle: '注册卡',
    addCardSubtitle: '注册一张新卡用于支付',
    creatingCheckout: '准备支付表单...',
    confirmingRegistration: '注册卡片...',
    successTitle: '卡片已注册',
    successMessage: '您的新卡已成功注册。',
    errorTitle: '注册失败',
    errorMessage: '注册卡片时发生错误。',
    retry: '重试',
    done: '完成',
    cancel: '取消',
    nicknameLabel: '是否要添加昵称?(可选)',
    skipNickname: '不带昵称保存',
    saveWithNickname: '带昵称保存',
    cardNumberLabel: '卡号',
    expiryMonthLabel: '月',
    expiryYearLabel: '年',
    cvvLabel: 'CVV',
    holderLabel: '持卡人',
    invalidCard: '卡号无效',
    invalidExpiry: '到期日期无效',
    invalidCvv: 'CVV 无效',
    invalidHolder: '请输入持卡人姓名',
    errorCardLimitExceeded: '您已达到最大卡片数量。请删除一张现有卡并重试。',
    errorDuplicateCard: '此卡已注册。',
    errorCardInactive: '此卡已停用。',
    errorCardExpired: '此卡已过期。',
    errorCardNotFound: '未找到卡片。',
    errorRegistrationFailed: '注册卡片失败。请重试。',
  },

  ja: {
    paymentMethod: '支払い方法',
    selectCard: 'カードを選択してください',
    registerNewCard: '新しいカードを登録',
    defaultCard: 'デフォルトカード',
    expired: '期限切れ',
    loading: '読み込み中...',
    noCards: '登録されたカードがありません',
    editNickname: 'カード名を編集',
    editNicknameSubtitle: 'このカードを簡単に識別できる名前を設定してください',
    nicknamePlaceholder: '例：個人カード、会社カード',
    clearNickname: 'カード名を削除',
    save: '保存',
    saving: '保存中...',
    saveError: 'カード名の保存に失敗しました。もう一度お試しください。',
    deleteCardTitle: 'カードを削除',
    deleteCardMessage: 'このカードを削除してよろしいですか？この操作は元に戻せません。',
    deleteCardDefaultWarning: 'これはデフォルトカードです。他のカードがデフォルトに設定されます。',
    delete: '削除',
    deleting: '削除中...',
    addCardTitle: 'カード登録',
    addCardSubtitle: '支払いに使用する新しいカードを登録します',
    creatingCheckout: '支払いフォームを準備中...',
    confirmingRegistration: 'カードを登録中...',
    successTitle: 'カード登録完了',
    successMessage: '新しいカードが正常に登録されました。',
    errorTitle: '登録失敗',
    errorMessage: 'カードの登録中にエラーが発生しました。',
    retry: '再試行',
    done: '完了',
    cancel: 'キャンセル',
    nicknameLabel: 'カード名を追加しますか？(オプション)',
    skipNickname: 'カード名なしで保存',
    saveWithNickname: 'カード名と一緒に保存',
    cardNumberLabel: 'カード番号',
    expiryMonthLabel: '月',
    expiryYearLabel: '年',
    cvvLabel: 'CVV',
    holderLabel: 'カード名義',
    invalidCard: 'カード番号が無効です',
    invalidExpiry: '有効期限が無効です',
    invalidCvv: 'CVVが無効です',
    invalidHolder: 'カード名義を入力してください',
    errorCardLimitExceeded: 'カードの登録上限に達しました。既存のカードを削除してから再試行してください。',
    errorDuplicateCard: 'このカードはすでに登録されています。',
    errorCardInactive: 'このカードは無効です。',
    errorCardExpired: 'このカードは期限切れです。',
    errorCardNotFound: 'カードが見つかりません。',
    errorRegistrationFailed: 'カードの登録に失敗しました。もう一度お試しください。',
  },

  hi: {
    paymentMethod: 'भुगतान विधि',
    selectCard: 'एक कार्ड चुनें',
    registerNewCard: 'नया कार्ड पंजीकृत करें',
    defaultCard: 'डिफ़ॉल्ट कार्ड',
    expired: 'समाप्त',
    loading: 'लोड हो रहा है...',
    noCards: 'कोई कार्ड पंजीकृत नहीं है',
    editNickname: 'कार्ड उपनाम संपादित करें',
    editNicknameSubtitle: 'इस कार्ड को आसानी से पहचानने के लिए नाम सेट करें',
    nicknamePlaceholder: 'उदा: व्यक्तिगत कार्ड, कार्य कार्ड',
    clearNickname: 'उपनाम हटाएं',
    save: 'सहेजें',
    saving: 'सहेज रहा है...',
    saveError: 'उपनाम सहेजने में विफल। कृपया पुनः प्रयास करें।',
    deleteCardTitle: 'कार्ड हटाएं',
    deleteCardMessage: 'क्या आप वाकई इस कार्ड को हटाना चाहते हैं? यह क्रिया पूर्ववत नहीं की जा सकती।',
    deleteCardDefaultWarning: 'यह आपका डिफ़ॉल्ट कार्ड है। दूसरे कार्ड को डिफ़ॉल्ट के रूप में सेट किया जाएगा।',
    delete: 'हटाएं',
    deleting: 'हटा रहा है...',
    addCardTitle: 'कार्ड पंजीकृत करें',
    addCardSubtitle: 'भुगतान के लिए नया कार्ड पंजीकृत करें',
    creatingCheckout: 'भुगतान फॉर्म तैयार किया जा रहा है...',
    confirmingRegistration: 'कार्ड पंजीकृत किया जा रहा है...',
    successTitle: 'कार्ड पंजीकृत',
    successMessage: 'आपका नया कार्ड सफलतापूर्वक पंजीकृत किया गया है।',
    errorTitle: 'पंजीकरण विफल',
    errorMessage: 'कार्ड पंजीकृत करते समय एक त्रुटि हुई।',
    retry: 'पुनः प्रयास',
    done: 'पूर्ण',
    cancel: 'रद्द करें',
    nicknameLabel: 'क्या आप उपनाम जोड़ना चाहते हैं? (वैकल्पिक)',
    skipNickname: 'उपनाम के बिना सहेजें',
    saveWithNickname: 'उपनाम के साथ सहेजें',
    cardNumberLabel: 'कार्ड नंबर',
    expiryMonthLabel: 'महीना',
    expiryYearLabel: 'वर्ष',
    cvvLabel: 'CVV',
    holderLabel: 'कार्डधारक',
    invalidCard: 'अमान्य कार्ड नंबर',
    invalidExpiry: 'अमान्य समाप्ति तिथि',
    invalidCvv: 'अमान्य CVV',
    invalidHolder: 'कार्डधारक का नाम दर्ज करें',
    errorCardLimitExceeded: 'आप कार्डों की अधिकतम संख्या तक पहुंच गए हैं। कृपया एक मौजूदा कार्ड हटाएं और पुनः प्रयास करें।',
    errorDuplicateCard: 'यह कार्ड पहले से पंजीकृत है।',
    errorCardInactive: 'यह कार्ड निष्क्रिय है।',
    errorCardExpired: 'यह कार्ड समाप्त हो गया है।',
    errorCardNotFound: 'कार्ड नहीं मिला।',
    errorRegistrationFailed: 'कार्ड पंजीकृत करने में विफल। कृपया पुनः प्रयास करें।',
  },

  de: {
    paymentMethod: 'Zahlungsmethode',
    selectCard: 'Wählen Sie eine Karte',
    registerNewCard: 'Neue Karte registrieren',
    defaultCard: 'Standardkarte',
    expired: 'Abgelaufen',
    loading: 'Lädt...',
    noCards: 'Keine registrierten Karten',
    editNickname: 'Kartenspitzname bearbeiten',
    editNicknameSubtitle: 'Legen Sie einen Namen fest, um diese Karte leicht zu identifizieren',
    nicknamePlaceholder: 'z.B. Privatkarte, Geschäftskarte',
    clearNickname: 'Spitznamen entfernen',
    save: 'Speichern',
    saving: 'Wird gespeichert...',
    saveError: 'Spitzname konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
    deleteCardTitle: 'Karte löschen',
    deleteCardMessage: 'Möchten Sie diese Karte wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    deleteCardDefaultWarning: 'Dies ist Ihre Standardkarte. Eine andere Karte wird als Standard festgelegt.',
    delete: 'Löschen',
    deleting: 'Wird gelöscht...',
    addCardTitle: 'Karte registrieren',
    addCardSubtitle: 'Registrieren Sie eine neue Karte für Zahlungen',
    creatingCheckout: 'Zahlungsformular wird vorbereitet...',
    confirmingRegistration: 'Karte wird registriert...',
    successTitle: 'Karte registriert',
    successMessage: 'Ihre neue Karte wurde erfolgreich registriert.',
    errorTitle: 'Registrierung fehlgeschlagen',
    errorMessage: 'Bei der Registrierung der Karte ist ein Fehler aufgetreten.',
    retry: 'Wiederholen',
    done: 'Fertig',
    cancel: 'Abbrechen',
    nicknameLabel: 'Möchten Sie einen Spitznamen hinzufügen? (Optional)',
    skipNickname: 'Ohne Spitznamen speichern',
    saveWithNickname: 'Mit Spitznamen speichern',
    cardNumberLabel: 'Kartennummer',
    expiryMonthLabel: 'Monat',
    expiryYearLabel: 'Jahr',
    cvvLabel: 'CVV',
    holderLabel: 'Karteninhaber',
    invalidCard: 'Ungültige Kartennummer',
    invalidExpiry: 'Ungültiges Ablaufdatum',
    invalidCvv: 'Ungültiger CVV',
    invalidHolder: 'Karteninhaber eingeben',
    errorCardLimitExceeded: 'Sie haben die maximale Anzahl von Karten erreicht. Löschen Sie eine vorhandene Karte und versuchen Sie es erneut.',
    errorDuplicateCard: 'Diese Karte ist bereits registriert.',
    errorCardInactive: 'Diese Karte ist inaktiv.',
    errorCardExpired: 'Diese Karte ist abgelaufen.',
    errorCardNotFound: 'Karte nicht gefunden.',
    errorRegistrationFailed: 'Registrierung der Karte fehlgeschlagen. Bitte versuchen Sie es erneut.',
  },

  it: {
    paymentMethod: 'Metodo di pagamento',
    selectCard: 'Seleziona una carta',
    registerNewCard: 'Registra nuova carta',
    defaultCard: 'Carta predefinita',
    expired: 'Scaduta',
    loading: 'Caricamento...',
    noCards: 'Nessuna carta registrata',
    editNickname: 'Modifica nome della carta',
    editNicknameSubtitle: 'Imposta un nome per identificare facilmente questa carta',
    nicknamePlaceholder: 'Es. Carta personale, Carta aziendale',
    clearNickname: 'Rimuovi nome',
    save: 'Salva',
    saving: 'Salvataggio...',
    saveError: 'Salvataggio del nome fallito. Per favore, riprova.',
    deleteCardTitle: 'Elimina carta',
    deleteCardMessage: 'Sei sicuro di voler eliminare questa carta? Questa azione non può essere annullata.',
    deleteCardDefaultWarning: 'Questa è la tua carta predefinita. Un’altra carta verrà impostata come predefinita.',
    delete: 'Elimina',
    deleting: 'Eliminazione...',
    addCardTitle: 'Registra carta',
    addCardSubtitle: 'Registra una nuova carta per i pagamenti',
    creatingCheckout: 'Preparazione del modulo di pagamento...',
    confirmingRegistration: 'Registrazione della carta...',
    successTitle: 'Carta registrata',
    successMessage: 'La tua nuova carta è stata registrata con successo.',
    errorTitle: 'Registrazione fallita',
    errorMessage: 'Si è verificato un errore durante la registrazione della carta.',
    retry: 'Riprova',
    done: 'Fatto',
    cancel: 'Annulla',
    nicknameLabel: 'Vuoi aggiungere un soprannome? (Facoltativo)',
    skipNickname: 'Salva senza soprannome',
    saveWithNickname: 'Salva con soprannome',
    cardNumberLabel: 'Numero carta',
    expiryMonthLabel: 'Mese',
    expiryYearLabel: 'Anno',
    cvvLabel: 'CVV',
    holderLabel: 'Intestatario',
    invalidCard: 'Numero carta non valido',
    invalidExpiry: 'Data di scadenza non valida',
    invalidCvv: 'CVV non valido',
    invalidHolder: 'Inserisci l’intestatario della carta',
    errorCardLimitExceeded: 'Hai raggiunto il numero massimo di carte. Elimina una carta esistente e riprova.',
    errorDuplicateCard: 'Questa carta è già registrata.',
    errorCardInactive: 'Questa carta è inattiva.',
    errorCardExpired: 'Questa carta è scaduta.',
    errorCardNotFound: 'Carta non trovata.',
    errorRegistrationFailed: 'Registrazione della carta fallita. Per favore, riprova.',
  },

  ru: {
    paymentMethod: 'Способ оплаты',
    selectCard: 'Выберите карту',
    registerNewCard: 'Зарегистрировать новую карту',
    defaultCard: 'Карта по умолчанию',
    expired: 'Истёк срок',
    loading: 'Загрузка...',
    noCards: 'Нет зарегистрированных карт',
    editNickname: 'Изменить псевдоним карты',
    editNicknameSubtitle: 'Установите имя для удобной идентификации этой карты',
    nicknamePlaceholder: 'Например: Личная карта, Рабочая карта',
    clearNickname: 'Удалить псевдоним',
    save: 'Сохранить',
    saving: 'Сохранение...',
    saveError: 'Не удалось сохранить псевдоним. Пожалуйста, повторите попытку.',
    deleteCardTitle: 'Удалить карту',
    deleteCardMessage: 'Вы уверены, что хотите удалить эту карту? Это действие нельзя отменить.',
    deleteCardDefaultWarning: 'Это ваша карта по умолчанию. Другая карта будет установлена как карта по умолчанию.',
    delete: 'Удалить',
    deleting: 'Удаление...',
    addCardTitle: 'Зарегистрировать карту',
    addCardSubtitle: 'Зарегистрируйте новую карту для платежей',
    creatingCheckout: 'Подготовка формы оплаты...',
    confirmingRegistration: 'Регистрация карты...',
    successTitle: 'Карта зарегистрирована',
    successMessage: 'Ваша новая карта успешно зарегистрирована.',
    errorTitle: 'Регистрация не удалась',
    errorMessage: 'Произошла ошибка при регистрации карты.',
    retry: 'Повторить',
    done: 'Готово',
    cancel: 'Отмена',
    nicknameLabel: 'Хотите добавить псевдоним? (Необязательно)',
    skipNickname: 'Сохранить без псевдонима',
    saveWithNickname: 'Сохранить с псевдонимом',
    cardNumberLabel: 'Номер карты',
    expiryMonthLabel: 'Месяц',
    expiryYearLabel: 'Год',
    cvvLabel: 'CVV',
    holderLabel: 'Держатель карты',
    invalidCard: 'Недействительный номер карты',
    invalidExpiry: 'Недействительная дата истечения',
    invalidCvv: 'Недействительный CVV',
    invalidHolder: 'Укажите держателя карты',
    errorCardLimitExceeded: 'Вы достигли максимального количества карт. Удалите существующую карту и повторите попытку.',
    errorDuplicateCard: 'Эта карта уже зарегистрирована.',
    errorCardInactive: 'Эта карта неактивна.',
    errorCardExpired: 'Срок действия этой карты истёк.',
    errorCardNotFound: 'Карта не найдена.',
    errorRegistrationFailed: 'Не удалось зарегистрировать карту. Пожалуйста, повторите попытку.',
  },

  ar: {
    paymentMethod: 'طريقة الدفع',
    selectCard: 'اختر بطاقة',
    registerNewCard: 'تسجيل بطاقة جديدة',
    defaultCard: 'البطاقة الافتراضية',
    expired: 'منتهية الصلاحية',
    loading: 'جاري التحميل...',
    noCards: 'لا توجد بطاقات مسجلة',
    editNickname: 'تعديل اسم البطاقة',
    editNicknameSubtitle: 'حدد اسماً لتحديد هذه البطاقة بسهولة',
    nicknamePlaceholder: 'مثال: بطاقة شخصية، بطاقة العمل',
    clearNickname: 'إزالة الاسم',
    save: 'حفظ',
    saving: 'جاري الحفظ...',
    saveError: 'فشل حفظ الاسم. يرجى المحاولة مرة أخرى.',
    deleteCardTitle: 'حذف البطاقة',
    deleteCardMessage: 'هل أنت متأكد من حذف هذه البطاقة؟ لا يمكن التراجع عن هذا الإجراء.',
    deleteCardDefaultWarning: 'هذه هي بطاقتك الافتراضية. سيتم تعيين بطاقة أخرى كبطاقة افتراضية.',
    delete: 'حذف',
    deleting: 'جاري الحذف...',
    addCardTitle: 'تسجيل بطاقة',
    addCardSubtitle: 'سجل بطاقة جديدة للدفع',
    creatingCheckout: 'جاري تجهيز نموذج الدفع...',
    confirmingRegistration: 'جاري تسجيل البطاقة...',
    successTitle: 'تم تسجيل البطاقة',
    successMessage: 'تم تسجيل بطاقتك الجديدة بنجاح.',
    errorTitle: 'فشل التسجيل',
    errorMessage: 'حدث خطأ أثناء تسجيل البطاقة.',
    retry: 'إعادة المحاولة',
    done: 'تم',
    cancel: 'إلغاء',
    nicknameLabel: 'هل تريد إضافة اسم؟ (اختياري)',
    skipNickname: 'حفظ بدون اسم',
    saveWithNickname: 'حفظ مع اسم',
    cardNumberLabel: 'رقم البطاقة',
    expiryMonthLabel: 'الشهر',
    expiryYearLabel: 'السنة',
    cvvLabel: 'CVV',
    holderLabel: 'حامل البطاقة',
    invalidCard: 'رقم البطاقة غير صالح',
    invalidExpiry: 'تاريخ الانتهاء غير صالح',
    invalidCvv: 'رمز CVV غير صالح',
    invalidHolder: 'أدخل اسم حامل البطاقة',
    errorCardLimitExceeded: 'لقد وصلت إلى الحد الأقصى لعدد البطاقات. احذف بطاقة موجودة وحاول مرة أخرى.',
    errorDuplicateCard: 'هذه البطاقة مسجلة بالفعل.',
    errorCardInactive: 'هذه البطاقة غير نشطة.',
    errorCardExpired: 'انتهت صلاحية هذه البطاقة.',
    errorCardNotFound: 'البطاقة غير موجودة.',
    errorRegistrationFailed: 'فشل تسجيل البطاقة. يرجى المحاولة مرة أخرى.',
  },

  vi: {
    paymentMethod: 'Phương thức thanh toán',
    selectCard: 'Chọn một thẻ',
    registerNewCard: 'Đăng ký thẻ mới',
    defaultCard: 'Thẻ mặc định',
    expired: 'Hết hạn',
    loading: 'Đang tải...',
    noCards: 'Không có thẻ đã đăng ký',
    editNickname: 'Chỉnh sửa biệt danh thẻ',
    editNicknameSubtitle: 'Đặt tên để dễ dàng nhận diện thẻ này',
    nicknamePlaceholder: 'Ví dụ: Thẻ cá nhân, Thẻ công ty',
    clearNickname: 'Xóa biệt danh',
    save: 'Lưu',
    saving: 'Đang lưu...',
    saveError: 'Không thể lưu biệt danh. Vui lòng thử lại.',
    deleteCardTitle: 'Xóa thẻ',
    deleteCardMessage: 'Bạn có chắc chắn muốn xóa thẻ này? Hành động này không thể hoàn tác.',
    deleteCardDefaultWarning: 'Đây là thẻ mặc định của bạn. Thẻ khác sẽ được đặt làm mặc định.',
    delete: 'Xóa',
    deleting: 'Đang xóa...',
    addCardTitle: 'Đăng ký thẻ',
    addCardSubtitle: 'Đăng ký thẻ mới để thanh toán',
    creatingCheckout: 'Đang chuẩn bị biểu mẫu thanh toán...',
    confirmingRegistration: 'Đang đăng ký thẻ...',
    successTitle: 'Đã đăng ký thẻ',
    successMessage: 'Thẻ mới của bạn đã được đăng ký thành công.',
    errorTitle: 'Đăng ký thất bại',
    errorMessage: 'Đã xảy ra lỗi khi đăng ký thẻ.',
    retry: 'Thử lại',
    done: 'Hoàn tất',
    cancel: 'Hủy',
    nicknameLabel: 'Bạn có muốn thêm biệt danh? (Tuỳ chọn)',
    skipNickname: 'Lưu không có biệt danh',
    saveWithNickname: 'Lưu với biệt danh',
    cardNumberLabel: 'Số thẻ',
    expiryMonthLabel: 'Tháng',
    expiryYearLabel: 'Năm',
    cvvLabel: 'CVV',
    holderLabel: 'Chủ thẻ',
    invalidCard: 'Số thẻ không hợp lệ',
    invalidExpiry: 'Ngày hết hạn không hợp lệ',
    invalidCvv: 'CVV không hợp lệ',
    invalidHolder: 'Nhập tên chủ thẻ',
    errorCardLimitExceeded: 'Bạn đã đạt số lượng thẻ tối đa. Vui lòng xóa thẻ hiện có và thử lại.',
    errorDuplicateCard: 'Thẻ này đã được đăng ký.',
    errorCardInactive: 'Thẻ này không hoạt động.',
    errorCardExpired: 'Thẻ này đã hết hạn.',
    errorCardNotFound: 'Không tìm thấy thẻ.',
    errorRegistrationFailed: 'Đăng ký thẻ thất bại. Vui lòng thử lại.',
  },

  th: {
    paymentMethod: 'วิธีการชำระเงิน',
    selectCard: 'เลือกบัตร',
    registerNewCard: 'ลงทะเบียนบัตรใหม่',
    defaultCard: 'บัตรเริ่มต้น',
    expired: 'หมดอายุ',
    loading: 'กำลังโหลด...',
    noCards: 'ไม่มีบัตรที่ลงทะเบียน',
    editNickname: 'แก้ไขชื่อเล่นบัตร',
    editNicknameSubtitle: 'ตั้งชื่อเพื่อระบุบัตรนี้ได้ง่าย',
    nicknamePlaceholder: 'เช่น บัตรส่วนตัว บัตรงาน',
    clearNickname: 'ลบชื่อเล่น',
    save: 'บันทึก',
    saving: 'กำลังบันทึก...',
    saveError: 'บันทึกชื่อเล่นล้มเหลว โปรดลองอีกครั้ง',
    deleteCardTitle: 'ลบบัตร',
    deleteCardMessage: 'คุณแน่ใจหรือไม่ที่จะลบบัตรนี้? การดำเนินการนี้ไม่สามารถยกเลิกได้',
    deleteCardDefaultWarning: 'นี่คือบัตรเริ่มต้นของคุณ บัตรอื่นจะถูกตั้งเป็นค่าเริ่มต้น',
    delete: 'ลบ',
    deleting: 'กำลังลบ...',
    addCardTitle: 'ลงทะเบียนบัตร',
    addCardSubtitle: 'ลงทะเบียนบัตรใหม่สำหรับการชำระเงิน',
    creatingCheckout: 'กำลังเตรียมแบบฟอร์มการชำระเงิน...',
    confirmingRegistration: 'กำลังลงทะเบียนบัตร...',
    successTitle: 'ลงทะเบียนบัตรเรียบร้อย',
    successMessage: 'บัตรใหม่ของคุณได้รับการลงทะเบียนเรียบร้อยแล้ว',
    errorTitle: 'การลงทะเบียนล้มเหลว',
    errorMessage: 'เกิดข้อผิดพลาดขณะลงทะเบียนบัตร',
    retry: 'ลองอีกครั้ง',
    done: 'เสร็จสิ้น',
    cancel: 'ยกเลิก',
    nicknameLabel: 'คุณต้องการเพิ่มชื่อเล่น? (ไม่บังคับ)',
    skipNickname: 'บันทึกโดยไม่มีชื่อเล่น',
    saveWithNickname: 'บันทึกพร้อมชื่อเล่น',
    cardNumberLabel: 'หมายเลขบัตร',
    expiryMonthLabel: 'เดือน',
    expiryYearLabel: 'ปี',
    cvvLabel: 'CVV',
    holderLabel: 'ชื่อผู้ถือบัตร',
    invalidCard: 'หมายเลขบัตรไม่ถูกต้อง',
    invalidExpiry: 'วันหมดอายุไม่ถูกต้อง',
    invalidCvv: 'CVV ไม่ถูกต้อง',
    invalidHolder: 'กรอกชื่อผู้ถือบัตร',
    errorCardLimitExceeded: 'คุณได้ถึงจำนวนบัตรสูงสุดแล้ว กรุณาลบบัตรที่มีอยู่และลองอีกครั้ง',
    errorDuplicateCard: 'บัตรนี้ลงทะเบียนแล้ว',
    errorCardInactive: 'บัตรนี้ไม่ได้ใช้งาน',
    errorCardExpired: 'บัตรนี้หมดอายุแล้ว',
    errorCardNotFound: 'ไม่พบบัตร',
    errorRegistrationFailed: 'ลงทะเบียนบัตรล้มเหลว โปรดลองอีกครั้ง',
  },

  nl: {
    paymentMethod: 'Betaalmethode',
    selectCard: 'Selecteer een kaart',
    registerNewCard: 'Nieuwe kaart registreren',
    defaultCard: 'Standaardkaart',
    expired: 'Verlopen',
    loading: 'Laden...',
    noCards: 'Geen kaarten geregistreerd',
    editNickname: 'Bijnaam van kaart bewerken',
    editNicknameSubtitle: 'Stel een naam in om deze kaart eenvoudig te identificeren',
    nicknamePlaceholder: 'Bijv. Persoonlijke kaart, Werkkaart',
    clearNickname: 'Bijnaam verwijderen',
    save: 'Opslaan',
    saving: 'Opslaan...',
    saveError: 'Bijnaam opslaan mislukt. Probeer het opnieuw.',
    deleteCardTitle: 'Kaart verwijderen',
    deleteCardMessage: 'Weet u zeker dat u deze kaart wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
    deleteCardDefaultWarning: 'Dit is uw standaardkaart. Een andere kaart wordt als standaard ingesteld.',
    delete: 'Verwijderen',
    deleting: 'Verwijderen...',
    addCardTitle: 'Kaart registreren',
    addCardSubtitle: 'Registreer een nieuwe kaart voor betalingen',
    creatingCheckout: 'Betalingsformulier wordt voorbereid...',
    confirmingRegistration: 'Kaart wordt geregistreerd...',
    successTitle: 'Kaart geregistreerd',
    successMessage: 'Uw nieuwe kaart is succesvol geregistreerd.',
    errorTitle: 'Registratie mislukt',
    errorMessage: 'Er is een fout opgetreden bij het registreren van de kaart.',
    retry: 'Opnieuw proberen',
    done: 'Klaar',
    cancel: 'Annuleren',
    nicknameLabel: 'Wilt u een bijnaam toevoegen? (Optioneel)',
    skipNickname: 'Opslaan zonder bijnaam',
    saveWithNickname: 'Opslaan met bijnaam',
    cardNumberLabel: 'Kaartnummer',
    expiryMonthLabel: 'Maand',
    expiryYearLabel: 'Jaar',
    cvvLabel: 'CVV',
    holderLabel: 'Kaarthouder',
    invalidCard: 'Ongeldig kaartnummer',
    invalidExpiry: 'Ongeldige vervaldatum',
    invalidCvv: 'Ongeldige CVV',
    invalidHolder: 'Voer de naam van de kaarthouder in',
    errorCardLimitExceeded: 'U heeft het maximale aantal kaarten bereikt. Verwijder een bestaande kaart en probeer het opnieuw.',
    errorDuplicateCard: 'Deze kaart is al geregistreerd.',
    errorCardInactive: 'Deze kaart is inactief.',
    errorCardExpired: 'Deze kaart is verlopen.',
    errorCardNotFound: 'Kaart niet gevonden.',
    errorRegistrationFailed: 'Registratie van de kaart mislukt. Probeer het opnieuw.',
  },
};

/**
 * 로케일에 따른 번역 가져오기
 */
export function getSavedCardsTranslation(locale: Locale): SavedCardsTranslation {
  return savedCardsTranslations[locale];
}
